import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as echarts from 'echarts';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'zen-theme';
export const ECHARTS_DARK_THEME = 'zen-dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _current$ = new BehaviorSubject<ThemeMode>('light');
  readonly current$ = this._current$.asObservable();

  constructor() {
    this.registerEchartsTheme();
    const stored = (localStorage.getItem(STORAGE_KEY) as ThemeMode | null) ?? 'light';
    this.apply(stored);
  }

  get current(): ThemeMode {
    return this._current$.value;
  }

  toggle(): void {
    this.apply(this.current === 'dark' ? 'light' : 'dark');
  }

  setTheme(mode: ThemeMode): void {
    this.apply(mode);
  }

  private apply(mode: ThemeMode): void {
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem(STORAGE_KEY, mode);
    this._current$.next(mode);
  }

  private registerEchartsTheme(): void {
    echarts.registerTheme(ECHARTS_DARK_THEME, {
      backgroundColor: 'transparent',
      textStyle: { color: '#E6E8EE' },
      title: { textStyle: { color: '#F5F7F9' }, subtextStyle: { color: '#AFB4C0' } },
      legend: { textStyle: { color: '#E6E8EE' } },
      categoryAxis: {
        axisLine: { lineStyle: { color: '#3C4250' } },
        axisTick: { lineStyle: { color: '#3C4250' } },
        axisLabel: { color: '#AFB4C0' },
        splitLine: { lineStyle: { color: '#2A2E38' } },
        splitArea: { areaStyle: { color: ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.04)'] } }
      },
      valueAxis: {
        axisLine: { lineStyle: { color: '#3C4250' } },
        axisTick: { lineStyle: { color: '#3C4250' } },
        axisLabel: { color: '#AFB4C0' },
        splitLine: { lineStyle: { color: '#2A2E38' } },
        splitArea: { areaStyle: { color: ['rgba(255,255,255,0.02)', 'rgba(255,255,255,0.04)'] } }
      },
      tooltip: {
        backgroundColor: '#1C1F27',
        borderColor: '#3C4250',
        textStyle: { color: '#E6E8EE' }
      },
      dataZoom: {
        backgroundColor: 'rgba(255,255,255,0.04)',
        dataBackgroundColor: 'rgba(255,255,255,0.10)',
        fillerColor: 'rgba(0,124,221,0.25)',
        handleColor: '#0082C6',
        handleSize: '100%',
        textStyle: { color: '#AFB4C0' }
      }
    });
  }
}
